import { z } from "zod";
/**
 * Plan tier mirrors the Prisma `PlanTier` enum (schema.prisma §16). MVP only
 * exposes FREE and PRO at the API boundary; AGENCY is reserved for Phase 2 but
 * kept in the type because the schema enum allows it (see §16.1 scope cut).
 */
export const planTierSchema = z.enum(["FREE", "PRO", "AGENCY"]);
/**
 * IANA timezone validator. Uses `Intl.DateTimeFormat` to verify the runtime
 * actually understands the zone — equivalent to `Intl.supportedValuesOf` but
 * doesn't depend on it being available (it landed in Node 18 with patchy
 * coverage on older runtimes; the constructor-throws check is universal).
 *
 * `validateIanaTimezone` is exported separately so the backend can re-use the
 * same check inside repository code (e.g. defensive validation before write).
 */
export function isIanaTimezone(value) {
    if (typeof value !== "string" || value.length === 0)
        return false;
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
export const userTimezoneSchema = z
    .string()
    .min(1)
    .refine(isIanaTimezone, {
    message: "Invalid IANA timezone",
});
/**
 * Full /api/users/me response. `email` is optional in the User schema (nullable
 * column); cached `username`, `email`, and `languageCode` may be null while
 * `firstName` and `timezone` are always populated (firstName is required by
 * Telegram on every WebApp user; timezone defaults to "UTC" at upsert).
 */
export const userProfileSchema = z.object({
    id: z.string().min(1),
    telegramId: z.string().regex(/^\d+$/),
    firstName: z.string().min(1),
    username: z.string().nullable(),
    email: z.string().email().nullable(),
    timezone: z.string().min(1),
    languageCode: z.string().nullable(),
    plan: planTierSchema,
});
/**
 * PATCH /api/users/me body. Strict mode rejects unknown keys so a typo (or a
 * client trying to update plan or telegramId) fails loudly with a 400. All
 * three fields are optional; `email` and `languageCode` accept `null` to clear
 * the cached value, while `timezone` must be a valid IANA zone string when
 * present (no clearing — the column is NOT NULL with a default of "UTC").
 */
export const updateUserProfileSchema = z
    .object({
    email: z.string().email().nullable().optional(),
    timezone: userTimezoneSchema.optional(),
    languageCode: z.string().min(2).max(10).nullable().optional(),
})
    .strict();
//# sourceMappingURL=users.js.map